import React from 'react';
import { BsCheckLg, BsSquare } from 'react-icons/bs';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Flex, HStack, Icon, Text } from '@chakra-ui/react';
import { startCase, toLower } from 'lodash';
import PropTypes from 'prop-types';

import { CypressIds } from '../../../../../../../../constants/cypressIds';
import { hideTray, showTray } from '../../../../../../../../redux/modules/tray';
import colors from '../../../../../../../../styles/theme/foundation/colors';
import RequirementTemplateBlueprintType from '../../../../../../../../types/RequirementTemplateBlueprintType';
import { get } from '../../../../../../../../utils/arrayUtil';
import { getDisplayValueForType } from '../../../../../../../../utils/tableUtil';
import LoadingSpinner from '../../../../../../../Spinner';
import SetPolicyFieldValuesTray
  from '../../../../../RequirementTemplateLeftBox/components/RequirementTemplateBuilderBodyLeft/components/TemplateBuilderPolicyPanel/components/SetPolicyFieldValuesTray';
import TemplateBlockFooter from '../TemplateBlockFooter';

const InsurancePolicyTemplateBlock = ({ templateBlock, blueprint }) => {
  const dispatch = useDispatch();
  const currentPolicyTypeId = get(templateBlock.requirementTemplateBlockProperties, '0.requirement.insurancePolicyTypeId');
  const currentPolicy = useSelector(blueprint.selectPolicyTypeById(currentPolicyTypeId));

  const booleanProperties = templateBlock?.requirementTemplateBlockProperties
    .filter(blockProperty => (blockProperty?.requirement?.dataType === 'boolean'));
  const nonBooleanProperties = templateBlock?.requirementTemplateBlockProperties
    .filter(blockProperty => (blockProperty?.requirement?.dataType !== 'boolean'));

  const onRequiredToggle = isChecked => {
    templateBlock.required = isChecked ? 1 : 0;
    dispatch(blueprint.updateRequirementTemplateBlock(templateBlock.id, templateBlock));
  };

  const onBlockDelete = () => {
    dispatch(blueprint.deleteRequirementTemplateBlock(templateBlock.id));
  };

  const showPolicyTray = async () => {
    dispatch(blueprint.setCurrentRequirementTemplateBlock(templateBlock.id));
    await dispatch(blueprint.setCurrentPolicyType(currentPolicy));
    dispatch(showTray(
      () => <SetPolicyFieldValuesTray blueprint={blueprint} currentPolicyTypeId={currentPolicyTypeId} />,
      {
        onClose: async () => {
          if (!templateBlock.requirementTemplateBlockProperties.filter(p => p.value).length) {
            await dispatch(blueprint.deleteRequirementTemplateBlock(templateBlock.id));
          }

          dispatch(blueprint.resetCurrentPolicyType());
          dispatch(blueprint.setCurrentRequirementTemplateBlock(null));
          dispatch(hideTray());
        }
      }
    ));
  };

  if (templateBlock) {
    return (
      <Box
        _hover={{ boxShadow: 'hover2' }}
        borderRadius="10px"
        boxShadow="sharp4"
        data-cy={CypressIds.policyTypeBlock(currentPolicyTypeId)}
        flexBasis="100%"
        layerStyle="panel"
        mb={4}
        minH="100px"
        minW="480px"
        mr={4}
        mt={1}
        textAlign="left"
        w="100%"
      >
        { get(templateBlock, 'requirementTemplateBlockProperties.0.policyTypeDisplayName', '') && (
        <Flex
          borderBottom={`{1px solid ${colors.greyScale['200']}`}
          direction="column"
          justifyContent="flex-start"
          mb={2}
          mt={2}
          w="100%"
        >
          <Box pt={6} px={6} onClick={showPolicyTray}>
            <Text color={colors.greyScale['900']} textStyle="bodyMd600">
              {
              get(templateBlock, 'requirementTemplateBlockProperties.0.policyTypeDisplayName', '')
            }
            </Text>

            <HStack alignItems="flex-start" mt={3} w="100%">
              <Flex direction="column" w="45%">
                <Text color={colors.greyScale['500']} textAlign="left" textStyle="bodyXs500">
                  Required Coverage:
                </Text>
                {
                (booleanProperties?.length > 0) && booleanProperties.map(booleanProperty => {
                  if (booleanProperty?.requirement) {
                    if (get(booleanProperty, 'value', 0) > 0) {
                      return (
                        <HStack alignItems="flex-start" key={booleanProperty.id} mt={2}>
                          <Icon
                            as={get(booleanProperty, 'value', 0) > 0 ? BsCheckLg : BsSquare}
                            boxSize="10px"
                            color={colors.primaryScale['600']}
                            mt="5px"
                          />
                          <Box
                            color={colors.greyScale['900']}
                            overflowWrap="anywhere"
                            textStyle="bodySm500"
                          >
                            {startCase(toLower(get(booleanProperty, 'requirement.displayName', '')))}
                          </Box>
                        </HStack>
                      );
                    }
                  }
                })
              }
              </Flex>
              <Flex direction="column" w="55%">
                {
                (nonBooleanProperties.length > 0) && nonBooleanProperties.map(property => {
                  if (property?.requirement) {
                    return (
                      <HStack key={property.id} mt={2}>
                        <Text color={colors.greyScale['500']} textAlign="right" textStyle="bodyXs" w="60%">
                          {`${startCase(toLower(get(property, 'requirement.displayName', '')))}: `}
                        </Text>
                        <Text color={colors.greyScale['900']} textStyle="bodySm500" w="40%">
                          {`${property.value
                            ? getDisplayValueForType(property.value, get(property, 'requirement.dataType', '')) : '-'}`}
                        </Text>
                      </HStack>
                    );
                  }
                })
              }
              </Flex>
            </HStack>
          </Box>
          <TemplateBlockFooter
            blueprint={blueprint}
            cypressId={CypressIds.policyTypeDeleteButton(currentPolicyTypeId)}
            isChecked={templateBlock?.required > 0}
            requirementType="insurance_policy_type"
            requirementTypeId={currentPolicyTypeId}
            templateBlock={templateBlock}
            onChange={onRequiredToggle}
            onDelete={onBlockDelete}
          />
        </Flex>
        )}
        {!templateBlock?.requirementTemplateBlockProperties.length && (
        <Flex alignItems="center" height="100px" justifyContent="center" p={8}>
          <LoadingSpinner isInline />
        </Flex>
        )}
      </Box>
    );
  }

  return null;
};

InsurancePolicyTemplateBlock.propTypes = {
  blueprint: RequirementTemplateBlueprintType,
  templateBlock: PropTypes.shape({
    required: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
    requirementTemplateBlockProperties: PropTypes.arrayOf(PropTypes.shape({}))
  })
};

export default InsurancePolicyTemplateBlock;
